import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleRepairService } from 'src/app/services/repair/vehicle-repair.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-vehicle-repair',
  templateUrl: './add-edit-vehicle-repair.component.html',
  styleUrls: ['./add-edit-vehicle-repair.component.scss']
})
export class AddEditVehicleRepairComponent extends DestructorComponent implements OnInit {

  @Output() vehicleRepairAdded = new EventEmitter<void>();
  @Output() vehicleRepairUpdated = new EventEmitter<void>();

  vehicleRepairForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedVehicleRepair: any = null;
  private isVehicleDisabled: boolean = false;

  // Vehicle search properties
  vehicleSearchTerm: string = '';
  vehicleSearchResults: any[] = [];
  selectedVehicle: any = null;
  showVehicleDropdown: boolean = false;
  isVehicleSearching: boolean = false;
  private vehicleSearchSubject = new Subject<string>();

  // Status options
  statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Repaired', label: 'Repaired' }
  ];

  constructor(
    private fb: FormBuilder,
    private _vehicleRepairService: VehicleRepairService,
    private _vehicleService: VehicleService
  ) {
    super();
    this.initializeForm();
    this.setupVehicleSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.vehicleRepairForm = this.fb.group({
      vehicle_id: ['', [Validators.required]],
      vehicle_name: ['', [Validators.required]],
      company: [''],
      cost: ['', [Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      order_date: [''],
      complete_date: [''],
      status: ['Pending'],
      problems: [''],
      note: ['']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_add_edit_vehicle_repair');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const repairId = modalElement.getAttribute('data-repair-id');
        if (repairId) {
          this.isEditMode = true;
          this.isVehicleDisabled = true;
          this.loadVehicleRepairData(repairId);
        } else {
          this.isEditMode = false;
          this.selectedVehicleRepair = null;
          this.isVehicleDisabled = false;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  private loadVehicleRepairData(repairId: string): void {
    this.isLoading = true;
    this.subscriptions.push(
      this._vehicleRepairService.GetDataById(repairId).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.selectedVehicleRepair = response.data;
            this.populateForm(this.selectedVehicleRepair);
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading vehicle repair data:', error);
          this.handleError('Failed to load vehicle repair data');
        }
      })
    );
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.type == "alaedo" ? vehicle.pictures[1] : vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg';
  }

  private populateForm(repair: any): void {
    if (repair.vehicle) {
      this.selectedVehicle = repair.vehicle;
      this.vehicleSearchTerm = this.getVehicleDisplayName(repair.vehicle);
    }

    this.vehicleRepairForm.patchValue({
      vehicle_id: repair.vehicle_id || repair.vehicle?._id || repair.vehicle?.id || '',
      vehicle_name: this.vehicleSearchTerm,
      company: repair.company || '',
      cost: repair.cost || '',
      order_date: repair.order_date ? this.formatDateForInput(repair.order_date) : '',
      complete_date: repair.complete_date ? this.formatDateForInput(repair.complete_date) : '',
      status: repair.status || 'Pending',
      problems: repair.problems || '',
      note: repair.note || ''
    });
  }

  private formatDateForInput(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  private resetForm(): void {
    this.vehicleRepairForm.reset();
    this.vehicleRepairForm.patchValue({
      status: 'Pending'
    });
    this.selectedVehicle = null;
    this.vehicleSearchTerm = '';
    this.vehicleSearchResults = [];
    this.showVehicleDropdown = false;
    this.isVehicleSearching = false;
    this.isLoading = false;
    this.selectedVehicleRepair = null;
  }

  onSubmit(): void {
    if (this.vehicleRepairForm.valid && !this.isLoading) {
      this.isLoading = true;

      let formData: any = { ...this.vehicleRepairForm.value };

      // Remove display fields
      delete formData.vehicle_name;

      // Set vehicle_id from selected vehicle
      if (this.selectedVehicle) {
        formData.vehicle_id = this.selectedVehicle._id || this.selectedVehicle.id;
      }

      if (this.isEditMode && this.selectedVehicleRepair) {
        this.updateVehicleRepair(formData);
      } else {
        this.addVehicleRepair(formData);
      }
    } else {
      this.markFormGroupTouched(this.vehicleRepairForm);
    }
  }

  private addVehicleRepair(data: any): void {
    this.subscriptions.push(
      this._vehicleRepairService.AddData(data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Vehicle repair record added successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.vehicleRepairAdded.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to add vehicle repair record');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while adding vehicle repair record');
        }
      })
    );
  }

  private updateVehicleRepair(data: any): void {
    const repairId = this.selectedVehicleRepair._id || this.selectedVehicleRepair.id;
    this.subscriptions.push(
      this._vehicleRepairService.UpdateData(repairId, data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Vehicle repair record updated successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.vehicleRepairUpdated.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to update vehicle repair record');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while updating vehicle repair record');
        }
      })
    );
  }

  private handleError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_add_edit_vehicle_repair');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Vehicle search methods
  private setupVehicleSearchDebounce(): void {
    this.subscriptions.push(
      this.vehicleSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        if (searchTerm.trim()) {
          this.searchVehicles(searchTerm);
        } else {
          this.vehicleSearchResults = [];
          this.showVehicleDropdown = false;
        }
      })
    );
  }

  onVehicleSearchInput(event: any): void {
    if (this.isVehicleDisabled) {
      return;
    }

    const searchTerm = event.target.value;
    this.vehicleSearchTerm = searchTerm;
    this.vehicleSearchSubject.next(searchTerm);

    // Update form control value
    this.vehicleRepairForm.patchValue({
      vehicle_name: searchTerm
    });

    if (searchTerm.trim()) {
      this.showVehicleDropdown = true;
    } else {
      this.showVehicleDropdown = false;
      this.selectedVehicle = null;
      this.vehicleRepairForm.patchValue({
        vehicle_id: '',
        vehicle_name: ''
      });
    }
  }

  private searchVehicles(searchTerm: string): void {
    if (this.isVehicleDisabled) {
      return;
    }

    this.isVehicleSearching = true;
    const filters = { search: searchTerm };

    this.subscriptions.push(
      this._vehicleService.GetData('1', searchTerm, filters).subscribe({
        next: (response: any) => {
          this.isVehicleSearching = false;
          if (response.status && response.data) {
            this.vehicleSearchResults = response.data.data || response.data || [];
          } else {
            this.vehicleSearchResults = [];
          }
        },
        error: (error: any) => {
          this.isVehicleSearching = false;
          console.error('Error searching vehicles:', error);
          this.vehicleSearchResults = [];
        }
      })
    );
  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);
    this.showVehicleDropdown = false;

    this.vehicleRepairForm.patchValue({
      vehicle_id: vehicle._id || vehicle.id,
      vehicle_name: this.vehicleSearchTerm
    });
  }

  private getVehicleDisplayName(vehicle: any): string {
    if (!vehicle) return '';

    const parts = [];
    if (vehicle.car_no) parts.push(vehicle.car_no);
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.make) parts.push(vehicle.make);
    if (vehicle.model) parts.push(vehicle.model);
    if (vehicle.year) parts.push(vehicle.year);

    return parts.join(' - ') || vehicle._id || vehicle.id || 'Unknown Vehicle';
  }

  onVehicleInputFocus(): void {
    if (this.isVehicleDisabled) {
      return;
    }

    if (this.vehicleSearchTerm.trim() && this.vehicleSearchResults.length > 0) {
      this.showVehicleDropdown = true;
    }
  }

  onVehicleInputBlur(): void {
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      this.showVehicleDropdown = false;
    }, 200);
  }

  // Form control getters
  get vehicle_id() { return this.vehicleRepairForm.get('vehicle_id'); }
  get vehicle_name() { return this.vehicleRepairForm.get('vehicle_name'); }
  get company() { return this.vehicleRepairForm.get('company'); }
  get cost() { return this.vehicleRepairForm.get('cost'); }
  get order_date() { return this.vehicleRepairForm.get('order_date'); }
  get complete_date() { return this.vehicleRepairForm.get('complete_date'); }
  get status() { return this.vehicleRepairForm.get('status'); }
  get problems() { return this.vehicleRepairForm.get('problems'); }
  get note() { return this.vehicleRepairForm.get('note'); }

  // Utility getters
  get isVehicleSelectionDisabled(): boolean {
    return this.isVehicleDisabled;
  }
}
